import {
  InteractionRequiredAuthError,
  InteractionStatus
} from "@azure/msal-browser";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Dialog, DialogActions, DialogContent, Divider, Slide, Stack, Toolbar, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { callAuthApi } from './auth/api';
import RouterSetup from './common/components/RouterSetup';
import Footer from './common/features/Footer';
import Header from './common/features/Header';
import axios from './common/api/axiosConfig';
import { fetchChildBacFailure, fetchChildBacStart, resetChildBacs, setChildBacs } from './features/userprofile/childBacSlice';
import { setUser } from './features/userprofile/userSlice';
import log from './common/components/Logger'

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

function App() {
  const user = useSelector((state) => state.user?.data?.payload);
  const currentUrl = window.location.href.split('/').pop();
  console.log(currentUrl);
  console.log("App Rendering");
  console.log(user);

  return (
    <div>
      <AuthenticatedTemplate>
        <ProtectedComponentWithAuth />
        {user !== undefined && user !== null ? (
          user.role === 'INVALID' ? (
            <Dialog open={true} TransitionComponent={Transition} keepMounted aria-describedby="add-dialog-slide-description">
              <Toolbar>
                Unrecognized User
                <Box sx={{ flexGrow: 1 }}></Box>
              </Toolbar>
              <Divider />
              <DialogContent>
                <Stack direction="row" spacing={1} sx={{ p: 1 }}>
                  <Stack><WarningIcon /></Stack>
                  <Stack>
                    <Typography>
                      <span>Your Username and Password are not recognized, please contact your Fleet Administrator for assistance.</span><br />
                    </Typography>
                  </Stack>
                </Stack>
              </DialogContent>
              <DialogActions>
              </DialogActions>
            </Dialog>
          ) : (
            <div className="App">
              <header className="App-header">
                <div>
                  <Header />
                </div>
              </header>
              <RouterSetup />
              <div>
                <Footer />
              </div>
            </div>
          )
        ) : (<div>Loading...</div>)}
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        {currentUrl !== "logout" ? (
          <div>
            <LoginUser />
            <p>You are not signed in! Please sign in.</p>
          </div>
        ) : (<p>You have been logged out and can now close this window.</p>)}
      </UnauthenticatedTemplate>
    </div>
  );
}

// Wrap ProtectedComponent with auth protection
const ProtectedComponentWithAuth = withAuthProtection(ProtectedComponent);

function ProtectedComponent() {
  const { instance, inProgress, accounts } = useMsal();
  const dispatch = useDispatch();
  const apiList = useSelector((state) => state.apiList?.apiList);

  const getChildBacEndpoint = useCallback(() => {
    if (apiList?.getChildBacList) {
      return apiList.getChildBacList;
    }

    if (apiList?.getAuthUser) {
      return apiList.getAuthUser.replace('/getauthuser', '/childbaclistFromFsvm');
    }

    return '';
  }, [apiList]);

  const getFleetDealerChildBacEndpoint = useCallback(() => {
    if (apiList?.getFltChildBacList) {
      return apiList.getFltChildBacList;
    }

    if (apiList?.getAuthUser) {
      return apiList.getAuthUser.replace('/getauthuser', '/fleetDlrChildbaclistFromFsvm');
    }

    return '';
  }, [apiList]);

  // Original Upfitter function - unchanged
  const fetchAndStoreChildBacs = useCallback((userProfile) => {
    const partnerType = String(userProfile?.partnerType || '').toUpperCase();
    const parentBac = userProfile?.bac;

    if (partnerType !== 'UPFITTER' || !parentBac) {
      dispatch(resetChildBacs());
      return;
    }

    const endpoint = getChildBacEndpoint();
    if (!endpoint) {
      dispatch(fetchChildBacFailure({ parentBac, error: 'Child BAC endpoint is not configured.' }));
      return;
    }

    dispatch(fetchChildBacStart({ parentBac }));

    axios.post(endpoint, null, {
      params: { bac: parentBac }
    })
      .then((response) => {
        const values = response?.data?.childBac;
        const childBacs = Array.isArray(values)
          ? [...new Set(values.map((value) => String(value).trim()).filter((value) => value !== ''))].sort((a, b) => Number(a) - Number(b))
          : [];

        dispatch(setChildBacs({ parentBac, childBacs }));
      })
      .catch((error) => {
        const status = error?.response?.status;
        const message = error?.response?.data?.statusMessage || error?.response?.data?.message;
        dispatch(fetchChildBacFailure({
          parentBac,
          error: message || (status ? `Request failed with status ${status}` : 'Unable to fetch child BAC list')
        }));
      });
  }, [dispatch, getChildBacEndpoint]);

  // New FleetDealer function
  const fetchAndStoreFleetDealerChildBacs = useCallback((userProfile) => {
    const partnerType = String(userProfile?.partnerType || '').toUpperCase();
    const parentBac = userProfile?.bac;

    if (partnerType !== 'FLEETDEALER' || !parentBac) {
      return;
    }

    const endpoint = getFleetDealerChildBacEndpoint();
    if (!endpoint) {
      dispatch(fetchChildBacFailure({ parentBac, error: 'FleetDealer child BAC endpoint is not configured.' }));
      return;
    }

    dispatch(fetchChildBacStart({ parentBac }));

    axios.post(endpoint, null, {
      params: { bac: parentBac, partner: userProfile?.partnerType }
    })
      .then((response) => {
        const dealerList = response?.data?.dealerDataList;
        const childBacs = Array.isArray(dealerList)
          ? [...new Set(
              dealerList
                .map((dealer) => {
                  const bac = String(dealer?.bac || '').trim();
                  // Remove leading zeros
                  return bac.replace(/^0+/, '') || '0';
                })
                .filter((bac) => bac !== '' && bac !== parentBac && bac !== parentBac.replace(/^0+/, '')) // Exclude parent BAC
            )].sort((a, b) => Number(a) - Number(b))
          : [];

        dispatch(setChildBacs({ parentBac, childBacs }));
      })
      .catch((error) => {
        const status = error?.response?.status;
        const message = error?.response?.data?.statusMessage || error?.response?.data?.message;
        dispatch(fetchChildBacFailure({
          parentBac,
          error: message || (status ? `Request failed with status ${status}` : 'Unable to fetch FleetDealer child BAC list')
        }));
      });
  }, [dispatch, getFleetDealerChildBacEndpoint]);

  const callGetAuthAndStoreToken = useCallback((responseObject) => {
    console.log("In auth and store token");
    console.log(responseObject);
    
    let idToken = responseObject.idToken;
    let homeAccountId = responseObject.account.homeAccountId;
    localStorage.setItem("idToken", idToken);
    localStorage.setItem("homeAccountId", homeAccountId);
    
    // Call your API with token
    callAuthApi().then((response) => {
      if (response) {
        const userData = response.userProfile || response;
        if (userData) {
          dispatch(setUser(userData));
          // Call appropriate child BAC fetch function based on user type
          fetchAndStoreChildBacs(userData);
          fetchAndStoreFleetDealerChildBacs(userData);
          log.info(`fos_ui_log: App():ProtectedComponent(): Successful user fetch`);
        } else {
          log.error(`fos_ui_log: App():ProtectedComponent(): Invalid response structure`);
          // Set INVALID role if response structure is wrong
          dispatch(setUser({ role: 'INVALID', email: responseObject.account.username }));
        }
      }
    }).catch((apiError) => {
      log.error(`fos_ui_log: App():ProtectedComponent(): API call failed - ${apiError}`);
      
      // Check if it's a 401 or 403 authentication error
      const errorCode = typeof apiError?.status === 'number' ? apiError.status : null;
      
      if (errorCode === 401 || errorCode === 403) {
        dispatch(setUser({ 
          role: 'INVALID', 
          email: responseObject.account.username,
          errorCode: errorCode,
          errorMessage: apiError.message
        }));
        log.error(`fos_ui_log: Authentication error ${errorCode} - User not authorized`);
      }
      // User remains undefined and will continue showing "Loading..." screen
    });
  }, [dispatch, fetchAndStoreChildBacs]);

  useEffect(() => {
    const accessTokenRequest = {
      scopes: ["user.read"],
      account: accounts[0],
    };

    if (inProgress === InteractionStatus.None) {
      instance
        .acquireTokenSilent(accessTokenRequest)
        .then((accessTokenResponse) => {
          // Acquire token silent success
          callGetAuthAndStoreToken(accessTokenResponse);
        })
        .catch((error) => {
          log.error(`fos_ui_log: App():ProtectedComponent(): ${error}`);
          console.log("Inside catch block - attempting redirect");
          instance.acquireTokenRedirect(accessTokenRequest).then((accessTokenResponse) => {
            // Acquire token interactive success
            callGetAuthAndStoreToken(accessTokenResponse);
          }).catch((error) => {
            console.log("ProtectedComponent Error: ", error);
          });
        });
    }
  }, [instance, accounts, inProgress, callGetAuthAndStoreToken]);
}

function LoginUser() {
  const { instance } = useMsal();
  
  console.log("Logging in the user");

  useEffect(() => {
    const loginRequest = {
      scopes: ["user.read"],
    };

    instance.loginRedirect(loginRequest).catch(e => {
      console.error(e);
    });

    console.log("User has been logged in");
  }, [instance]);
}

/**
 * Custom hook to acquire and refresh MSAL tokens
 * Handles both silent and interactive token acquisition
 */
function useAcquireToken() {
  const { instance, accounts } = useMsal();

  const acquireToken = async () => {
    const request = {
      scopes: ["user.read"],
      account: accounts[0]
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      let idToken = response.idToken;
      let homeAccountId = response.account.homeAccountId;
      
      // Only update if token has changed
      const currentToken = localStorage.getItem("idToken");
      if (currentToken !== idToken) {
        localStorage.setItem("idToken", idToken);
        localStorage.setItem("homeAccountId", homeAccountId);
        log.info('fos_ui_log: Token refreshed silently');
      }
      
      return response.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        log.warn('fos_ui_log: Interactive token acquisition required');
        // Fallback to interactive method if silent token acquisition fails
        const response = await instance.acquireTokenPopup(request);
        let idToken = response.idToken;
        let homeAccountId = response.account.homeAccountId;
        localStorage.setItem("idToken", idToken);
        localStorage.setItem("homeAccountId", homeAccountId);
        return response.idToken;
      } else {
        log.error(`fos_ui_log: Token acquisition failed - ${error}`);
        throw error;
      }
    }
  };

  return acquireToken;
}

/**
 * Higher-Order Component (HOC) to wrap components with authentication protection
 * Handles automatic token refresh every 1 minute
 */
function withAuthProtection(WrappedComponent) {
  return function AuthProtectedComponent(props) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const acquireToken = useAcquireToken();

    // Initial authentication check
    useEffect(() => {
      async function checkAuth() {
        try {
          const token = await acquireToken();
          if (token) {
            setIsAuthenticated(true);
            log.info('fos_ui_log: Initial authentication successful');
          }
        } catch (error) {
          log.error(`fos_ui_log: Initial authentication failed - ${error}`);
          setIsAuthenticated(false);
        }
      }

      checkAuth();
    }, [acquireToken]);

    // Proactive token refresh every 1 minute
    useEffect(() => {
      const interval = setInterval(async () => {
        try {
          await acquireToken();
          log.info(`fos_ui_log: [${new Date().toLocaleString()}] Token refreshed proactively`);
        } catch (error) {
          log.error(`fos_ui_log: Failed to refresh token - ${error}`);
        }
      }, 1 * 60 * 1000); // 1 minute in milliseconds

      return () => clearInterval(interval); // Cleanup interval on component unmount
    }, [acquireToken]);

    // Refresh token when user returns to tab after being away
    useEffect(() => {
      const handleVisibilityChange = async () => {
        if (document.visibilityState === 'visible') {
          try {
            await acquireToken();
            log.info('fos_ui_log: Token refreshed on tab visibility change');
          } catch (error) {
            log.error(`fos_ui_log: Failed to refresh token on visibility change - ${error}`);
          }
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, [acquireToken]);

    if (!isAuthenticated) {
      return <div>Loading...</div>; // Show loading while authenticating
    }

    return <WrappedComponent {...props} />;
  };
}

export default App;


